import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, FileText, Image, X, Loader2 } from "lucide-react";
import api from "../lib/api";

const FILE_ICONS = { pdf: FileText, doc: FileText, docx: FileText, txt: FileText, csv: FileText, xls: FileText, xlsx: FileText };
const IMG_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

function getExt(name) { return name?.split(".").pop()?.toLowerCase() || ""; }
function formatSize(bytes) { return bytes < 1024 ? `${bytes}B` : bytes < 1048576 ? `${(bytes / 1024).toFixed(1)}KB` : `${(bytes / 1048576).toFixed(1)}MB`; }

export function FileUploader({ taskId, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/api/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (taskId) await api.post("/api/files/attach", { file_id: data.id, task_id: taskId });
      onUpload?.(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files[0]); };

  return (
    <div data-testid="file-uploader"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      style={{
        padding: "12px 14px", borderRadius: 12, cursor: uploading ? "wait" : "pointer",
        border: `1.5px dashed ${dragOver ? "var(--primary)" : "var(--bg-neutral)"}`,
        background: dragOver ? "var(--accent-subtle)" : "var(--bg-soft)",
        transition: "all 0.2s", display: "flex", alignItems: "center", gap: 8,
      }}>
      <input ref={inputRef} type="file" hidden onChange={(e) => upload(e.target.files[0])} />
      {uploading ? (
        <Loader2 size={14} color="var(--primary)" style={{ animation: "spin 1s linear infinite" }} />
      ) : (
        <Upload size={14} color={dragOver ? "var(--primary)" : "var(--text-muted)"} />
      )}
      <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: dragOver ? "var(--primary)" : "var(--text-muted)" }}>
        {uploading ? "Uploading..." : "Drop file or click to upload"}
      </span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function FileList({ files, onDelete }) {
  if (!files || files.length === 0) return null;
  return (
    <div data-testid="file-list" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {files.map(f => {
        const ext = getExt(f.original_filename);
        const isImg = IMG_EXTS.has(ext);
        const Icon = FILE_ICONS[ext] || (isImg ? Image : FileText);
        return (
          <motion.div key={f.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`file-item-${f.id}`}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
              borderRadius: 8, background: "rgba(255,255,255,0.5)",
              border: "1px solid var(--bg-neutral)", fontSize: "0.7rem",
            }}>
            <Icon size={13} color="var(--primary)" strokeWidth={1.5} />
            <span style={{ flex: 1, fontFamily: "'Satoshi', sans-serif", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.original_filename}
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.52rem", color: "var(--text-muted)" }}>
              {formatSize(f.size)}
            </span>
            {onDelete && (
              <button data-testid={`file-delete-${f.id}`} onClick={() => onDelete(f.id)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}>
                <X size={11} color="var(--text-muted)" />
              </button>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
